import { useGetCmsTheme } from "@/api/cms";
import {
  archivePuckPage,
  copyPuckBlockStrings,
  dehydrate,
  getPagePreviewUrl,
  loadPuckPage,
  publishPuckPage,
  savePuckPage,
  unpublishPuckPage,
  type PuckPageRecord,
} from "@/api/cms-puck/api";
import { PublicationHistoryDialog } from "@/components/cms-puck/editor/PublicationHistoryDialog";
import {
  PuckBlockClipboardActionBar,
  PuckBlockClipboardContextMenu,
  PuckBlockClipboardHotkeys,
  PuckBlockClipboardProvider,
} from "@/components/cms-puck/editor/PuckBlockClipboard";
import { FieldsPanel } from "@/components/cms-puck/fields/FieldsPanel";
import { MediaPickerHost } from "@/components/cms-puck/fields/MediaPickerField";
import { PuckImageStudioHost } from "@/components/cms-puck/fields/PuckImageStudioHost";
import { PuckDrawerHost } from "@/components/cms-puck/fields/PuckDrawerHost";
import { createPuckConfig } from "@/config/cms-puck/config";
import {
  getPuckNodeAtZoneIndex,
  mapDuplicateBlockIds,
  remapStringsForBlockCopy,
} from "@/lib/cms-puck/puck-tree";
// Styles de contenu CMS portés du site public → aperçu canvas WYSIWYG (scopés
// à `.cms-page-theme-scope`, cf. cms-canvas.css).
import "@/components/cms-puck/cms-canvas.css";
import { PagePublicationMenu } from "@/components/cms-puck/editor/PagePublicationMenu";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input";
import { PuckEditorLocaleProvider } from "@/context/cms-puck/PuckEditorLocaleContext";
import { collectMediaBaseValues } from "@/api/cms-puck/content-i18n";
import { PuckThemeProvider } from "@/context/cms-puck/PuckThemeContext";
import { CatalogFlowPreviewProvider } from "@/context/cms-puck/CatalogFlowPreviewContext";
import { CatalogItemPreviewProvider } from "@/context/cms-puck/CatalogItemPreviewContext";
import { SelectionCategoryPreviewProvider } from "@/context/cms-puck/SelectionCategoryPreviewContext";
import { SystemModelProvider } from "@/context/cms-puck/SystemModelContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import type { SupportedLocale } from "@/i18n";
import {
  useCmsNew,
  useTranslationBundleRevision,
} from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";
import {
  loadCmsGoogleFonts,
  useCmsGoogleFontsInFrame,
} from "@/lib/cms/cms-google-fonts";
import {
  isSystemModelPage,
  systemModelKindLabel,
} from "@/lib/cms-puck/system-model";
import { Puck, type Data } from "@measured/puck";
// Sans l'@import de rsms.me/inter : Inter est servie en local (assets/fonts).
import "@measured/puck/no-external.css";
import "@/components/cms-puck/cms-puck-chrome.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  ArrowLeft,
  Eye,
  History,
  LayoutTemplate,
  Monitor,
  Smartphone,
  Tablet,
} from "lucide-react";
import { listTemplates } from "@/api/cms-puck/templates";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

// L'éditeur Puck a son propre chrome plein écran (hors CMSLayout, qui charge
// normalement les polices CMS) : on lance ICI le chargement de la feuille
// Google Fonts de toutes les polices CMS, pour qu'une police choisie dans
// l'apparence / le style s'affiche sur le canevas. Sans bloquer le canevas :
// cf. loadCmsGoogleFonts.
void loadCmsGoogleFonts();

const EMPTY_DATA: Data = { content: [], root: { props: {} } };
const PAGES_KEY = ["cms-puck", "pages"] as const;
const pageKey = (id: string, lang: SupportedLocale) =>
  ["cms-puck", "page", id, lang] as const;

const EmptyHeaderActions = () => <></>;

function PuckFieldsOverride({
  children,
  itemSelector,
}: {
  children: ReactNode;
  itemSelector?: Parameters<typeof FieldsPanel>[0]["itemSelector"];
}) {
  return <FieldsPanel itemSelector={itemSelector}>{children}</FieldsPanel>;
}

function PuckCanvasOverride({ children }: { children: ReactNode }) {
  return (
    <CatalogFlowPreviewProvider>
      <CatalogItemPreviewProvider>
        <SelectionCategoryPreviewProvider>
          {children}
          <MediaPickerHost />
          <PuckImageStudioHost />
          <PuckBlockClipboardHotkeys />
        </SelectionCategoryPreviewProvider>
      </CatalogItemPreviewProvider>
    </CatalogFlowPreviewProvider>
  );
}

/** Canevas : polices CMS + raccourcis / menu contextuel clipboard (iframe). */
function PuckIframeOverride({
  children,
  document: frameDocument,
}: {
  children: ReactNode;
  document?: Document;
}) {
  useCmsGoogleFontsInFrame(frameDocument);
  return (
    <>
      {children}
      <PuckBlockClipboardHotkeys listenDocument={frameDocument ?? null} />
      <PuckBlockClipboardContextMenu listenDocument={frameDocument ?? null} />
    </>
  );
}

const PUCK_OVERRIDES = {
  headerActions: EmptyHeaderActions,
  iframe: PuckIframeOverride,
  fields: PuckFieldsOverride,
  puck: PuckCanvasOverride,
  actionBar: PuckBlockClipboardActionBar,
};

/**
 * Éditeur Puck d'une page (route `/cms/advanced/:id`). On garde notre propre
 * en-tête (retour, nom, statut, langue, brouillon, publier) et on neutralise le
 * bouton « Publish » interne de Puck (`overrides.headerActions`).
 *
 * i18n (R1/R3/R4/R5) : la STRUCTURE est partagée entre langues ; seul le texte
 * traduisible est par langue (`ContentString`). On charge la page pour la langue
 * sélectionnée (`getPage(id, lang)` → layout neutre réhydraté avec `strings`),
 * et on sauvegarde en déshydratant pour la langue courante.
 *
 * UX du changement de langue : on AUTO-SAUVE le brouillon de la langue courante
 * AVANT de basculer (`switchLang`), pour ne perdre aucune édition ; la bascule
 * remonte Puck (via `key={id-lang}`) sur le texte de l'autre langue.
 */
const CMSAdvancedEditor = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cmsNew = useCmsNew();
  const translationRevision = useTranslationBundleRevision();
  const viewports = useMemo(
    () => [
      {
        width: 390,
        height: "auto" as const,
        label: cmsNew("cms.new.editor.viewport.mobile", "Mobile"),
        icon: <Smartphone size={16} />,
      },
      {
        width: 768,
        height: "auto" as const,
        label: cmsNew("cms.new.editor.viewport.tablet", "Tablette"),
        icon: <Tablet size={16} />,
      },
      {
        width: 1536,
        height: "auto" as const,
        label: cmsNew("cms.new.editor.viewport.desktop", "Bureau"),
        icon: <Monitor size={16} />,
      },
    ],
    [cmsNew],
  );
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  // Thème CMS (palette + défauts) fourni aux custom fields d'apparence + à
  // l'aperçu du canvas via PuckThemeProvider. Même source que l'ancien CMS.
  const { data: cmsTheme } = useGetCmsTheme();

  // Langue actuellement éditée.
  const [lang, setLang] = useState<SupportedLocale>(defaultWebsiteLanguage);
  useEffect(() => {
    if (websiteLanguages.length && !websiteLanguages.includes(lang)) {
      setLang(defaultWebsiteLanguage);
    }
  }, [websiteLanguages, defaultWebsiteLanguage, lang]);

  // Puck est non-contrôlé : on capte la dernière data (texte EN CLAIR pour la
  // langue courante) via onChange.
  const liveData = useRef<Data>(EMPTY_DATA);
  const baselineStrings = useRef<Record<string, string>>({});
  const savedSnapshot = useRef<{ name: string; layout: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  /** Incrémenté après restauration d'une version → force le remount de Puck. */
  const [layoutRevision, setLayoutRevision] = useState(0);

  const templatesQuery = useQuery({
    queryKey: ["cms-puck", "templates"] as const,
    queryFn: () => listTemplates(),
  });

  const pageQuery = useQuery({
    queryKey: pageKey(id, lang),
    queryFn: () => loadPuckPage(id, lang),
    enabled: !!id,
  });

  const page = pageQuery.data;
  const pageReady = !!page && page.id === id;
  const pageIsSystemModel = isSystemModelPage(pageQuery.data);
  const modelKindLabel = pageIsSystemModel
    ? systemModelKindLabel(pageQuery.data)
    : null;

  const [pageName, setPageName] = useState("");
  useEffect(() => {
    if (page?.name) setPageName(page.name);
  }, [page?.name]);

  const puckConfig = useMemo(() => {
    // `translationRevision` sert de “trigger” : reconstruire la config
    // quand i18next charge les ressources (fallback → traductions).
    // `rootLabel` = titre header Puck (sidebar droite masquée via css).
    void translationRevision;
    const label = pageName.trim();
    return createPuckConfig(label ? { rootLabel: label } : undefined);
  }, [translationRevision, pageName]);

  const captureSavedSnapshot = useCallback((name: string, data: Data) => {
    const { neutralLayout } = dehydrate(data);
    savedSnapshot.current = {
      name: name.trim(),
      layout: JSON.stringify(neutralLayout),
    };
    setDirty(false);
  }, []);

  useEffect(() => {
    if (pageQuery.data) {
      liveData.current = pageQuery.data.data ?? EMPTY_DATA;
      baselineStrings.current = pageQuery.data.strings ?? {};
      captureSavedSnapshot(pageQuery.data.name ?? "", liveData.current);
    }
  }, [pageQuery.data, captureSavedSnapshot]);

  const recomputeDirty = useCallback((data: Data, name: string) => {
    const snap = savedSnapshot.current;
    if (!snap) {
      setDirty(false);
      return;
    }
    const { neutralLayout } = dehydrate(data);
    const changed =
      name.trim() !== snap.name ||
      JSON.stringify(neutralLayout) !== snap.layout;
    setDirty(changed);
  }, []);

  const saveMutation = useMutation({
    mutationFn: async ({ publish }: { publish: boolean }) => {
      const name = pageName.trim() || page?.name || "";
      await savePuckPage(id, {
        name,
        data: liveData.current,
        lang,
        baselineStrings: baselineStrings.current,
        defaultLang: defaultWebsiteLanguage,
        base: page?.neutralLayout,
      });
      if (publish) await publishPuckPage(id);
    },
    onSuccess: (_data, { publish }) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ["cms-puck", "page", id] });
      captureSavedSnapshot(pageName, liveData.current);
      toast({
        title: publish
          ? cmsNew("cms.new.auto.title.page_publiee", "Page publiée")
          : cmsNew(
              "cms.new.auto.title.brouillon_enregistre",
              "Brouillon enregistré",
            ),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({
        title: cmsNew(
          "cms.new.auto.title.echec_de_lenregistrement",
          "Échec de l'enregistrement",
        ),
        description: cmsNew(
          "cms.new.auto.description.verifie_ta_connexion_a_lapi",
          "Vérifie ta connexion à l'API.",
        ),
        variant: "error",
        duration: 4000,
      });
    },
  });

  // Bascule de langue : on persiste d'abord le brouillon de la langue courante
  // (texte structurel partagé + strings de la langue), puis on change la langue.
  // La requête `getPage(id, nextLang)` se relance via la queryKey (lang change).
  const [switching, setSwitching] = useState(false);
  const switchLang = async (next: SupportedLocale) => {
    if (next === lang || switching) return;
    setSwitching(true);
    try {
      if (pageReady) {
        await savePuckPage(id, {
          name: pageName.trim() || page?.name || "",
          data: liveData.current,
          lang,
          baselineStrings: baselineStrings.current,
          defaultLang: defaultWebsiteLanguage,
          base: page?.neutralLayout,
        });
        queryClient.invalidateQueries({ queryKey: PAGES_KEY });
        // On vide le cache de la langue qu'on vient d'éditer pour relire frais.
        queryClient.invalidateQueries({ queryKey: pageKey(id, lang) });
      }
      setLang(next);
    } catch {
      toast({
        title: cmsNew(
          "cms.new.auto.title.impossible_de_changer_de_langue",
          "Impossible de changer de langue",
        ),
        description: cmsNew(
          "cms.new.auto.description.lenregistrement_du_brouillon_a_echoue",
          "L'enregistrement du brouillon a échoué.",
        ),
        variant: "error",
        duration: 4000,
      });
    } finally {
      setSwitching(false);
    }
  };

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishPuckPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ["cms-puck", "page", id] });
      toast({
        title: cmsNew(
          "cms.new.auto.title.page_depubliee",
          "Page dépubliée (brouillon)",
        ),
        variant: "success",
        duration: 1600,
      });
    },
    onError: (err: unknown) => {
      const res = (
        err as { response?: { data?: { message?: string } } }
      )?.response;
      toast({
        title:
          res?.data?.message ??
          cmsNew(
            "cms.new.auto.title.echec_de_la_depublication",
            "Échec de la dépublication",
          ),
        variant: "error",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archivePuckPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: ["cms-puck", "page", id] });
      toast({
        title: cmsNew("cms.new.auto.title.page_archivee", "Page archivée"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: (err: unknown) => {
      const res = (
        err as { response?: { data?: { message?: string } } }
      )?.response;
      toast({
        title:
          res?.data?.message ??
          cmsNew(
            "cms.new.auto.title.echec_de_larchivage",
            "Échec de l'archivage",
          ),
        variant: "error",
      });
    },
  });

  const busy =
    saveMutation.isPending ||
    switching ||
    unpublishMutation.isPending ||
    archiveMutation.isPending ||
    previewing;

  const handleRestored = useCallback(
    (record: PuckPageRecord) => {
      const data = record.data ?? EMPTY_DATA;
      liveData.current = data;
      baselineStrings.current = record.strings ?? {};
      if (record.name) setPageName(record.name);
      captureSavedSnapshot(record.name ?? pageName, data);
      setLayoutRevision((v) => v + 1);
      void queryClient.setQueryData(pageKey(id, lang), record);
      void queryClient.invalidateQueries({
        queryKey: ["cms-puck", "page", id],
      });
    },
    [captureSavedSnapshot, id, lang, pageName, queryClient],
  );

  const handlePreviewDraft = async () => {
    if (!pageReady || previewing) return;
    setPreviewing(true);
    const tab = window.open("", "_blank");
    try {
      if (dirty) {
        await savePuckPage(id, {
          name: pageName.trim() || page?.name || "",
          data: liveData.current,
          lang,
          baselineStrings: baselineStrings.current,
        });
        captureSavedSnapshot(pageName, liveData.current);
        queryClient.invalidateQueries({ queryKey: ["cms-puck", "page", id] });
      }
      const { url } = await getPagePreviewUrl(id, lang);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
    } catch (err: unknown) {
      if (tab) tab.close();
      const msg = isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      toast({
        title: cmsNew(
          "cms.new.auto.title.previsualisation_impossible",
          "Prévisualisation impossible",
        ),
        description:
          msg ??
          cmsNew(
            "cms.new.editor.previewSaveRetry",
            "Enregistre le brouillon et réessaie.",
          ),
        variant: "error",
      });
    } finally {
      setPreviewing(false);
    }
  };

  const onStringsRemapped = useCallback((mappings: Record<string, string>) => {
    baselineStrings.current = remapStringsForBlockCopy(
      baselineStrings.current,
      mappings,
    );
  }, []);

  const handlePuckAction = (
    action: { type: string; sourceIndex?: number; sourceZone?: string },
    newState: { data: Data },
    prevState: { data: Data },
  ) => {
    if (action.type !== "duplicate") return;
    const zone = action.sourceZone;
    const index = action.sourceIndex;
    if (zone == null || index == null) return;

    const source = getPuckNodeAtZoneIndex(prevState.data, zone, index);
    const duplicate = getPuckNodeAtZoneIndex(newState.data, zone, index + 1);
    if (!source || !duplicate) return;

    const mappings = mapDuplicateBlockIds(source, duplicate);
    if (Object.keys(mappings).length === 0) return;

    onStringsRemapped(mappings);

    void copyPuckBlockStrings(id, mappings).catch(() => {
      toast({
        title: cmsNew(
          "cms.new.auto.title.traductions_non_copiees",
          "Traductions non copiées",
        ),
        description: cmsNew(
          "cms.new.auto.description.le_bloc_a_ete_duplique_mais_ses_traductions_nont_pas_pu_etre_copiees",
          "Le bloc a été dupliqué mais ses traductions n'ont pas pu être copiées.",
        ),
        variant: "error",
        duration: 3500,
      });
    });
  };

  return (
    <SystemModelProvider
      value={{
        isSystemModel: pageIsSystemModel,
        kindLabel: modelKindLabel,
      }}
    >
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/cms/advanced")}
        >
          <ArrowLeft size={16} className="mr-1" />
          {cmsNew("cms.new.editor.backToPages", "Pages")}
        </Button>
        <Input
          className="h-8 max-w-xs text-sm font-semibold"
          value={pageName}
          disabled={!pageReady || busy}
          onChange={(e) => {
            setPageName(e.target.value);
            recomputeDirty(liveData.current, e.target.value);
          }}
          placeholder={
            pageIsSystemModel
              ? cmsNew(
                  "cms.new.systemModel.namePlaceholder",
                  "Nom du modèle",
                )
              : cmsNew(
                  "cms.new.auto.placeholder.nom_de_la_page",
                  "Nom de la page",
                )
          }
        />
        {page && (
          <Badge
            variant={
              page.status === "PUBLISHED"
                ? "default"
                : page.status === "ARCHIVED"
                  ? "outlined"
                  : "secondary"
            }
          >
            {puckPageStatusLabel(cmsNew, page.status)}
          </Badge>
        )}
        {dirty && (
          <Badge variant="outlined" className="border-amber-300 text-amber-700">
            {cmsPhrase(cmsNew, "dirtyUnsaved")}
          </Badge>
        )}
        {pageIsSystemModel ? (
          <Badge
            variant="outlined"
            className="flex-row items-center gap-1.5 border-slate-300 text-slate-700"
          >
            <LayoutTemplate size={12} className="shrink-0" />
            <span>
              {cmsNew("cms.new.systemModel.editorChip", "Modèle système")}
              {modelKindLabel ? ` · ${modelKindLabel}` : null}
            </span>
          </Badge>
        ) : page?.sourceTemplateKey ? (
          <Badge variant="secondary" className="flex-row items-center gap-1.5">
            <LayoutTemplate size={12} className="shrink-0" />
            <span>
              {cmsNew("cms.new.templates.fromStarterChip", "Créée depuis")}
              {": "}
              {templatesQuery.data?.find((t) => t.key === page.sourceTemplateKey)
                ?.name ?? page.sourceTemplateKey}
            </span>
          </Badge>
        ) : null}

        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
          <span className="shrink-0 text-xs text-slate-500">
            {cmsNew("cms.new.auto.text.langue", "Langue")}
          </span>
          <MultilingualSelect
            values={websiteLanguages}
            value={lang}
            disabled={busy}
            onValueChange={(v) => void switchLang(v as SupportedLocale)}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!pageReady || busy}
            onClick={() => void handlePreviewDraft()}
          >
            <Eye size={14} className="mr-1" />
            {cmsNew("cms.new.editor.preview", "Aperçu")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!pageReady || busy}
            onClick={() => setHistoryOpen(true)}
          >
            <History size={14} className="mr-1" />
            {cmsNew("cms.new.editor.versions", "Versions")}
          </Button>
          <PagePublicationMenu
            pageReady={pageReady}
            pageStatus={page?.status}
            dirty={dirty}
            busy={busy}
            isSaving={saveMutation.isPending}
            isUnpublishing={unpublishMutation.isPending}
            isArchiving={archiveMutation.isPending}
            onSaveDraft={() => saveMutation.mutate({ publish: false })}
            onPublish={() => saveMutation.mutate({ publish: true })}
            onUnpublish={() => unpublishMutation.mutate()}
            onArchive={() => archiveMutation.mutate()}
          />
        </div>
      </div>

      <div style={{ height: "calc(100vh - 130px)" }}>
        {pageQuery.isLoading || !pageReady || switching ? (
          <div className="flex h-full items-center justify-center text-slate-400">
            {pageQuery.isError
              ? cmsNew("cms.new.editor.pageNotFound", "Page introuvable.")
              : switching
                ? cmsNew(
                    "cms.new.editor.switchingLang",
                    "Changement de langue…",
                  )
                : cmsNew(
                    "cms.new.editor.loadingPage",
                    "Chargement de la page…",
                  )}
          </div>
        ) : (
          <PuckThemeProvider theme={cmsTheme ?? null}>
            <PuckEditorLocaleProvider
              value={{
                locale: lang,
                defaultLocale: defaultWebsiteLanguage,
                mediaBase: page?.neutralLayout
                  ? collectMediaBaseValues(page.neutralLayout)
                  : new Map(),
              }}
            >
              <PuckBlockClipboardProvider
                pageId={id}
                onStringsRemapped={onStringsRemapped}
              >
                <Puck
                  // `translationRevision` dans la key : Puck est non-contrôlé et
                  // capture `config` au montage — sans remount, les libellés
                  // (labels de blocs, options de champs pilotées par le config)
                  // resteraient figés sur le fallback si le bundle i18n charge
                  // APRÈS le montage. Le revision ne bouge qu'au démarrage / au
                  // changement de langue (déjà géré par `lang`), donc pas de perte
                  // d'édition en cours de session.
                  key={`${id}-${lang}-${layoutRevision}-${translationRevision}`}
                  config={puckConfig}
                  data={page.data ?? EMPTY_DATA}
                  viewports={viewports}
                  onChange={(data) => {
                    liveData.current = data;
                    recomputeDirty(data, pageName);
                  }}
                  onAction={handlePuckAction}
                  overrides={PUCK_OVERRIDES}
                />
              </PuckBlockClipboardProvider>
            </PuckEditorLocaleProvider>
            {/* Host des drawers de champs : monté HORS de <Puck> (sibling), PAS
                dans overrides.puck. Il n'utilise pas `usePuck` (writeback via
                l'onChange capturé), donc rien à faire dans l'arbre de Puck — et
                surtout : y monter un formulaire lourd (Sortable @dnd-kit)
                imbriquait un DndContext dans celui de Puck → conflit → re-render
                en boucle + reload du canva à l'ouverture. Hors de l'arbre Puck,
                le drawer est immunisé contre les re-render/remount de l'éditeur. */}
            <PuckDrawerHost />
            <PublicationHistoryDialog
              pageId={id}
              lang={lang}
              open={historyOpen}
              onOpenChange={setHistoryOpen}
              onRestored={handleRestored}
            />
          </PuckThemeProvider>
        )}
      </div>
    </div>
    </SystemModelProvider>
  );
};

export default CMSAdvancedEditor;
